/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import { makeStyles, Theme } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';
import SettingsCard from 'components/@common/Cards/SettingsCard';
import SettingsSlider from 'components/@common/Slider';
import GET_KEYBOARDING_STUDENT_SETTINGS from 'graph/settings/key/get-keyboarding-student-settings-query';
import UPDATE_KEY_STUDENT_STOP_AND_REVIEW_SETTING from 'graph/settings/key/update-key-student-stop-and-review-setting-mutation';
import { updateCache } from 'graph/update-cache';
import useStore from 'store';
import {
  KeySettingStopAndReviewData,
  Student,
  WeekSettingData
} from 'types/schema-generated-types';
import getSettingSuccessAlert from '../../../utils/settings-success-alert';

interface StopAndReviewProps {
  student: Student;
  productCurriculumCode: string;
  setting: KeySettingStopAndReviewData & {
    [key: string]: Pick<WeekSettingData, 'week' | 'enabled'>;
  };
  expanded: string | null;
  handleChangeExpanded: (panel: string | null) => () => void;
}

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      paddingTop: theme.spacing(2),
      width: '100%'
    },
    switch: { marginLeft: 0 },
    sliderLabelTitle: { fontWeight: theme.typography.fontWeightRegular },
    sliderLabelValue: { fontWeight: theme.typography.fontWeightBold }
  };
});

const STOP_AND_REVIEW_MAX_WEEK = 36;

const getStudentKeySettingsMutationCacheUpdate = (studentId: number): any => ({
  update: updateCache<any>([
    {
      query: GET_KEYBOARDING_STUDENT_SETTINGS,
      variables: {
        where: {
          studentId
        }
      },
      reducer: (
        cachedData,
        { updateKeyStudentSettingStopAndReview }
      ): { keyStudentSettings: any } => {
        const [{ value: stopAndReview }] = updateKeyStudentSettingStopAndReview;
        return {
          keyStudentSettings: {
            ...cachedData?.keyStudentSettings,
            stopAndReview
          }
        };
      }
    }
  ])
});

const StopAndReview: FC<StopAndReviewProps> = ({
  student,
  productCurriculumCode,
  setting,
  expanded,
  handleChangeExpanded
}) => {
  const classes = useStyles();
  const [, { openAlert }] = useStore();
  const { t } = useTranslation('StopAndReviewSetting');

  const stopReviewValue: any = {
    enabled: setting?.[productCurriculumCode]?.enabled ?? false,
    currentWeek: setting?.[productCurriculumCode]?.week ?? STOP_AND_REVIEW_MAX_WEEK
  };

  const [enableStopAndReview, setEnableStopAndReview] = useState(stopReviewValue.enabled);
  const [value, setValue] = useState<number | number[]>(stopReviewValue.currentWeek);

  const [updateKeyStudentSettingStopAndReview] = useMutation(
    UPDATE_KEY_STUDENT_STOP_AND_REVIEW_SETTING,
    getStudentKeySettingsMutationCacheUpdate(student.id)
  );

  const handleChangeSlider = (event: any, newValue: number | number[]): void => {
    setValue(newValue);
  };

  const handleToggle = (): void => setEnableStopAndReview(!enableStopAndReview);

  const handleOnCancel = (): void => {
    setEnableStopAndReview(stopReviewValue.enabled);
    setValue(stopReviewValue.currentWeek);
  };

  const handleOnConfirm = async (): Promise<void> => {
    try {
      await updateKeyStudentSettingStopAndReview({
        variables: {
          data: {
            studentId: student.id,
            value: {
              productCurriculumCode,
              enabled: enableStopAndReview,
              week: value
            }
          }
        }
      });
      openAlert(getSettingSuccessAlert('Stop & Review', t));
    } catch (error) {
      //TODO
      console.log('Error');
    }
  };

  return (
    <SettingsCard
      title={t('Stop & Review')}
      description={t(
        'Set a point in the curriculum for the student to stop and review previously completed activities.'
      )}
      expanded={expanded}
      panel="stopAndReview"
      handleChangeExpanded={handleChangeExpanded}
      onCancel={handleOnCancel}
      onSubmit={handleOnConfirm}
    >
      <FormControl component="fieldset" className={classes.container}>
        <FormControlLabel
          className={classes.switch}
          control={
            <Switch
              checked={enableStopAndReview}
              onChange={handleToggle}
              name="stopAndReview"
              color="primary"
            />
          }
          label={enableStopAndReview ? t('Enabled') : t('Disabled')}
        />
        {enableStopAndReview && (
          <div>
            <Typography id="stop-and-review-slider" gutterBottom variant="h6">
              <Typography variant="body1" component="p">
                {t(
                  'Move the slider to the week you want the student to stop and review previously completed activities.'
                )}
              </Typography>
              <Typography
                component="span"
                className={classes.sliderLabelTitle}
              >{`Current Week: `}</Typography>
              <Typography component="span" className={classes.sliderLabelValue}>
                {value}
              </Typography>
            </Typography>
            <SettingsSlider
              defaultValue={0}
              aria-labelledby="stop-and-review-slider"
              value={value}
              marks
              min={0}
              max={STOP_AND_REVIEW_MAX_WEEK}
              valueLabelDisplay="on"
              onChange={handleChangeSlider}
            />
          </div>
        )}
      </FormControl>
    </SettingsCard>
  );
};

export default StopAndReview;
